/**
 * First-touch attribution for organic growth (?ref=guest_<weddingId>, utm_*).
 *
 * Captured once on the first marketing landing, kept in localStorage for
 * 90 days and sent with registration (backend re-validates it). First-party
 * only: nothing here talks to a third party. Guest pages never capture, so a
 * guest opening a shared invitation is not attributed.
 */

export const ATTRIBUTION_KEY = 'handfastly-attribution';
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://handfastly.com').replace(/\/$/, '');

const MAX_AGE_MS = 90 * 86400000;
const MAX_VALUE_LENGTH = 100;
const SAFE_TEXT = /^[\w\-.~:+ @]+$/u;
const DEMO_ID = 'demo';

const PARAMS = Object.freeze([
  ['ref', 'ref'],
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
  ['template', 'template'],
]);

const GUEST_PATHS = [
  /^\/invite\//,
  /^\/rsvp\//,
  /^\/gallery\//,
  /^\/moments\//,
  /^\/find-seat\//,
];

export function isGuestPath(pathname) {
  return GUEST_PATHS.some((re) => re.test(pathname || ''));
}

function storage() {
  try {
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

function clean(value) {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim().slice(0, MAX_VALUE_LENGTH);
  return trimmed && SAFE_TEXT.test(trimmed) ? trimmed : undefined;
}

/** @returns {object|null} the stored first touch */
export function getAttribution() {
  try {
    const raw = storage()?.getItem(ATTRIBUTION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
}

export function clearAttribution() {
  try {
    storage()?.removeItem(ATTRIBUTION_KEY);
  } catch (_) {
    /* storage blocked */
  }
}

function isFresh(existing, now) {
  const at = Date.parse(existing?.capturedAt);
  return Number.isFinite(at) && now.getTime() - at < MAX_AGE_MS;
}

/**
 * Stores ref / utm params from a landing URL unless a fresh first touch exists.
 * @param {{ pathname: string, search: string }} location
 * @param {Date} [now]
 */
export function captureAttribution({ pathname, search }, now = new Date()) {
  if (isGuestPath(pathname)) return;
  const query = new URLSearchParams(search || '');
  const entries = PARAMS
    .map(([param, key]) => [key, clean(query.get(param) ?? undefined)])
    .filter(([, value]) => value !== undefined);
  if (entries.length === 0) return;
  if (isFresh(getAttribution(), now)) return;

  const touch = {
    ...Object.fromEntries(entries),
    landingPath: (pathname || '/').slice(0, 200),
    capturedAt: now.toISOString(),
  };
  try {
    storage()?.setItem(ATTRIBUTION_KEY, JSON.stringify(touch));
  } catch (_) {
    /* storage blocked — attribution is best-effort */
  }
}

/**
 * Link from a guest-facing page back to Handfastly, tagged for the guest loop.
 * @param {{ weddingId?: string|null, surface: string, path?: string, params?: Record<string,string> }} opts
 * @returns {string}
 */
export function guestRefUrl({ weddingId, surface, path = '/', params = {} }) {
  const url = new URL(path, SITE_URL);
  Object.entries(params).forEach(([k, v]) => {
    if (v) url.searchParams.set(k, v);
  });
  if (weddingId && weddingId !== DEMO_ID) url.searchParams.set('ref', `guest_${weddingId}`);
  url.searchParams.set('utm_source', 'guest_loop');
  url.searchParams.set('utm_medium', surface);
  return url.toString();
}
